/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/library/radio-group";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/library/avatar";
import { Label } from "@/components/ui/label";
import useRolesPipeline from "@/features/users/api/useRolesPipeline";
import { isBlockedUser } from "@/features/users/utils/blockedUsers";
import { AdvancedSkeleton } from "@/components/feedback/loading/Skeletons";
import { ErrorBlock } from "@/components/feedback/error/AlertError";
import { EmptyState } from "@/components/feedback/empty/EmptyState";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { setSelectedUser } from "@/features/leads/stores/leadFunctionalSlice";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  effectiveAssignKind,
  roleAcceptsAssignment,
  roleAcceptsBranchless,
} from "@/features/leads/hooks/useAssignationPermissions";
import {
  getAssignBlockReason,
  type AssignGateLead,
} from "@/features/leads/api/useAssignEligibility";
import { useEffect, useMemo } from "react";
import {
  buildRolePipelineParams,
  type AssignFilters,
} from "./FilterSection";

function userBranchAndCfSets(user: any) {
  const branchIds = new Set<number>(
    (user.branches ?? [])
      .filter((b: any) => !b.isRevoked)
      .map((b: any) => b.branchId ?? b.branch?.id)
      .map((id: unknown) => Number(id))
      .filter((n: number): n is number => !Number.isNaN(n))
  );
  const cfAccess = new Set<number>(
    (user.customFieldAccess ?? [])
      .map((a: any) => Number(a.customInputItemId))
      .filter((n: number): n is number => !Number.isNaN(n))
  );
  return { branchIds, cfAccess };
}

type SortedAssignableUser = {
  user: any;
  index: number;
  eligible: number;
  blockReason: ReturnType<typeof getAssignBlockReason>;
  ineligibleForAllMulti: boolean;
  isBlocked: boolean;
  isIneligible: boolean;
  notSelectable: boolean;
  hasRestriction: boolean;
  priority: number;
};

export default function UsersList({
  selectedRole,
  setSelectedRole,
  eligibility,
  totalLeads,
  eligibilityReliable = true,
  kind = "lead",
  assignGateLead = null,
  filters,
  searchTerm = "",
  selectedUserId: selectedUserIdProp,
  onSelectUser,
}: any & {
  eligibilityReliable?: boolean;
  assignGateLead?: AssignGateLead | null;
  filters?: AssignFilters;
  searchTerm?: string;
  /**
   * Controlled selection. The Assign drawer leaves these unset and the list
   * reads / writes `leadFunctional.selectedUser`; the Bulk Update drawer
   * passes both so its pick lives in `bulkUpdate.assignUserId` instead and
   * never bleeds into the Assign drawer's state. `onSelectUser(null)` is the
   * list clearing a pick it found ineligible.
   */
  selectedUserId?: string | number;
  onSelectUser?: (user: any | null) => void;
}) {
  const dispatch = useAppDispatch();
  const { selectedUser: reduxSelectedUser } = useAppSelector(
    (s) => s.leadFunctional
  );
  const selectedUserId =
    selectedUserIdProp !== undefined ? selectedUserIdProp : reduxSelectedUser;
  const select = (user: any | null) => {
    if (onSelectUser) onSelectUser(user);
    else dispatch(setSelectedUser(user ? user.id : ""));
  };
  const serverParams = useMemo(
    // forFilters drops roles the viewer's role EXCLUDES in the hierarchy
    // ladder — an excluded role must not be assignable from here either.
    () =>
      filters
        ? { ...buildRolePipelineParams(filters), forFilters: 1 }
        : { withLeadCount: "true", forFilters: 1 },
    [filters],
  );
  const { rolesPipeline, isLoading, error, refetch } = useRolesPipeline(
    undefined,
    serverParams,
  );

  const role = rolesPipeline?.find((r: any) => r.id === selectedRole);
  // Role-level "Inbound — receive branchless leads" toggle — one value for
  // every user in this list (mirrors server `roleAllowsBranchlessAssignment`).
  const acceptsBranchless = useMemo(() => roleAcceptsBranchless(role), [role]);
  // Belt-and-suspenders: even if a stale `selectedRole` survives a kind switch,
  // never list users from a role whose inbound flag is off.
  const users = useMemo(() => {
    if (!role || !roleAcceptsAssignment(role, effectiveAssignKind(role, kind)))
      return [];
    const status = filters?.status ?? "active";
    const term = searchTerm.trim().toLowerCase();
    return (role.users ?? []).filter((u: any) => {
      // Blocked = hard ban: they can't sign in and the server refuses to
      // assign to them, so they are not shown here at all (user directive
      // 2026-09-20). Unrelated to the Active/Inactive switch below, which is
      // about `isActive`. The blocked-user rendering further down stays as a
      // backstop for anything that slips through.
      if (isBlockedUser(u)) return false;
      if (status === "active" && u.isActive === false) return false;
      if (status === "inactive" && u.isActive !== false) return false;
      if (term && !String(u.name ?? "").toLowerCase().includes(term))
        return false;
      return true;
    });
  }, [kind, role, filters?.status, searchTerm]);
  // Multi-lead per-user counts come from the cache-derived `eligibility`
  // map, which is only complete when every selected lead is cached. After a
  // "select all" it isn't, so we suppress the (misleading) per-user "X of Y"
  // counts and restriction badges here — the server-backed confirmation
  // modal reports the real split. The single-lead `assignGateLead` path is
  // unaffected (that lead is always cached).
  const showEligibility = eligibilityReliable && totalLeads > 1;

  const sortedUsers = useMemo<SortedAssignableUser[]>(() => {
    return (users ?? [])
      .map((user: any, index: number) => {
        const eligible = eligibility[user.id] ?? totalLeads;
        const { branchIds, cfAccess } = userBranchAndCfSets(user);
        const blockReason = assignGateLead
          ? getAssignBlockReason(
              assignGateLead,
              branchIds,
              cfAccess,
              acceptsBranchless
            )
          : null;
        const ineligibleForAllMulti =
          !assignGateLead && showEligibility && eligible === 0;
        const isBlocked = !!user.isBlocked;
        const isIneligible =
          !isBlocked && (blockReason != null || ineligibleForAllMulti);
        const hasRestriction =
          showEligibility && eligible < totalLeads && !ineligibleForAllMulti;
        const priority = isBlocked ? 3 : isIneligible ? 2 : hasRestriction ? 1 : 0;

        return {
          user,
          index,
          eligible,
          blockReason,
          ineligibleForAllMulti,
          isBlocked,
          isIneligible,
          notSelectable: isBlocked || isIneligible,
          hasRestriction,
          priority,
        };
      })
      .sort((a: SortedAssignableUser, b: SortedAssignableUser) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        const workload = filters?.workload ?? "none";
        if (workload === "least" || workload === "most") {
          const ac = Number(a.user.leadCount ?? 0);
          const bc = Number(b.user.leadCount ?? 0);
          if (ac !== bc) return workload === "least" ? ac - bc : bc - ac;
        }
        return a.index - b.index;
      });
  }, [
    assignGateLead,
    acceptsBranchless,
    eligibility,
    showEligibility,
    totalLeads,
    users,
    filters?.workload,
  ]);

  useEffect(() => {
    return () => {
      setSelectedRole("");
    };
  }, [setSelectedRole]);

  useEffect(() => {
    if (selectedUserId == null || selectedUserId === "") return;
    const uid = Number(selectedUserId);
    const u = users.find((x: any) => x.id === uid);
    if (!u) {
      select(null);
      return;
    }
    if (u.isBlocked) {
      select(null);
      return;
    }
    const { branchIds, cfAccess } = userBranchAndCfSets(u);
    if (assignGateLead) {
      if (
        getAssignBlockReason(
          assignGateLead,
          branchIds,
          cfAccess,
          acceptsBranchless
        ) != null
      ) {
        select(null);
      }
      return;
    }
    if (showEligibility) {
      const eligible = eligibility[uid] ?? totalLeads;
      if (eligible === 0) {
        select(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignGateLead,
    acceptsBranchless,
    users,
    selectedUserId,
    eligibility,
    totalLeads,
    showEligibility,
    dispatch,
  ]);

  if (isLoading) {
    return (
      <div className="pt-2 pb-4">
        <AdvancedSkeleton variant="table" rows={15} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <ErrorBlock message={error.message} retry={refetch} />
      </div>
    );
  }

  if (users?.length == 0) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <EmptyState
          title="No Users Found"
          description="No Users found for the current selection. You can modify the filters or create a new user."
        />
      </div>
    );
  }
  return (
    <RadioGroup
      value={
        selectedUserId != null && selectedUserId !== ""
          ? String(selectedUserId)
          : undefined
      }
    >
      {sortedUsers?.map((item) => {
        const {
          user,
          eligible,
          blockReason,
          isBlocked,
          isIneligible,
          notSelectable,
          hasRestriction,
        } = item;
        const row = (
          <div
            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors group ${
              isBlocked
                ? "border border-[#F8B4B4] bg-[#FEF2F2]/60 cursor-not-allowed opacity-75"
                : isIneligible
                  ? "border border-orange-200/80 bg-[#fff7ed]/80 cursor-not-allowed opacity-90"
                  : `cursor-pointer hover:bg-gray-50 ${
                      hasRestriction
                        ? "border border-amber-200 bg-amber-50/30"
                        : ""
                    }`
            }`}
            onClick={() => {
              if (notSelectable) return;
              select(user);
            }}
          >
            <RadioGroupItem
              value={String(user.id)}
              id={`user-${user.id}`}
              className="mt-1"
              disabled={notSelectable}
            />
            <Label
              htmlFor={`user-${user.id}`}
              className={`flex items-center gap-3 flex-1 pb-0 ${
                notSelectable ? "cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <Avatar
                className={`size-10 border ${isBlocked ? "grayscale" : ""}`}
              >
                <AvatarImage src={user?.avatar} alt={user.name} />
                <AvatarFallback className="bg-[#FFE0E0] text-black font-semibold">
                  {user.name.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-b3 ${isBlocked ? "text-[#7a1d2e]" : ""} ${isIneligible && !isBlocked ? "text-[#1e302a]/75" : ""}`}
                  >
                    {user.name}
                  </span>
                  {isBlocked && (
                    <span
                      className="px-1.5 py-0.5 text-[10px] font-medium rounded-full"
                      style={{
                        backgroundColor: "#FDE2E5",
                        color: "#922A3A",
                        border: "1px solid #F8B4B4",
                      }}
                    >
                      Blocked
                    </span>
                  )}
                </div>
                {isBlocked ? (
                  <span className="text-[11px] text-[#922A3A]">
                    Cannot be assigned to leads
                  </span>
                ) : isIneligible ? (
                  <span className="text-[11px] text-[#e8a24f]">
                    {blockReason === "branch"
                      ? "Not a member of this lead's branch."
                      : blockReason === "noBranch"
                        ? "Lead has no branch — this role can't receive branchless leads."
                        : blockReason === "customField"
                          ? "Missing custom field access for this lead."
                          : "Ineligible for all selected leads."}
                  </span>
                ) : (
                  hasRestriction && (
                    <span className="text-[11px] text-amber-600">
                      {eligible} of {totalLeads} leads
                    </span>
                  )
                )}
              </div>
            </Label>
          </div>
        );

        if (isBlocked) {
          return (
            <Tooltip key={user.id}>
              <TooltipTrigger asChild>{row}</TooltipTrigger>
              <TooltipContent side="left" sideOffset={8}>
                This user is blocked and cannot be assigned to leads.
              </TooltipContent>
            </Tooltip>
          );
        }

        if (isIneligible) {
          return <div key={user.id}>{row}</div>;
        }

        if (!showEligibility) return row;

        return (
          <Tooltip key={user.id}>
            <TooltipTrigger asChild>{row}</TooltipTrigger>
            <TooltipContent side="left" sideOffset={8}>
              {eligible === totalLeads
                ? `All ${totalLeads} leads will be assigned`
                : `${eligible} of ${totalLeads} leads can be assigned`}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </RadioGroup>
  );
}
